import { useState } from 'react';
import { ExternalLink, FileText } from 'lucide-react';
import { studioApi, useCancelInvoice, useCheckoutProblems, useIssueAct, useRemind, useResolveCheckout, useStudioInvoices, useStudioMe } from '@/api/studio';
import type { Invoice, InvoiceStatus } from '@/api/types';
import { DownloadButton } from '@/components/DownloadButton';
import { QueryPending } from '@/components/QueryState';
import { Dialog, PageHeader, Status } from '@/components/ui';
import { studioCan } from '@/lib/access';
import { useStudioTerms } from '@/lib/contour';
import { formatDate, formatDue, formatMoney, formatWhen } from '@/lib/format';
import { invoiceStatus, methodLabel, problemLabel } from '@/lib/status';
import { InvoiceDraftActions } from './InvoiceDraftActions';
import { MarkPaidDialog } from './MarkPaid';
import { Statement } from './StatementBlock';
import { Undelivered } from './Undelivered';
import { cancellable, clientsOf, markable, filterInvoices, noFilter, remindedRecently, sortLabel, type InvoiceFilter, type InvoiceSort } from './invoiceList';

export function StudioMoney() {
  const invoicesQ = useStudioInvoices();
  const invoices = invoicesQ.data;
  const remind = useRemind();
  const issueAct = useIssueAct();
  const { acts, contour } = useStudioTerms();
  const me = useStudioMe();
  const [filter, setFilter] = useState<InvoiceFilter>(noFilter);
  const [cancelling, setCancelling] = useState<Invoice>();
  const [marking, setMarking] = useState<Invoice>();
  // The bank statement is the owner's and only where the contour has one (RU): a manager would meet a 403, the main contour has no statement.
  const showStatement = !!contour?.statement && studioCan(me?.studioRole, 'team');
  const rows = invoices ? filterInvoices(invoices, filter) : [];
  const set = <K extends keyof InvoiceFilter>(k: K, v: InvoiceFilter[K]) => setFilter((f) => ({ ...f, [k]: v }));

  return (
    <>
      <PageHeader title="Счета" description="Все счета всех клиентов. Счёт выставляется из карточки клиента: свободные строки, каталог — как заготовки." />
      <PaymentProblems />
      <Undelivered />
      {invoices && showStatement && <Statement invoices={invoices} />}
      {!invoices ? (
        <QueryPending query={invoicesQ} />
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2 text-sm" role="search" aria-label="Фильтр счетов">
            <select className="field h-9 w-48 py-0" aria-label="Клиент" value={filter.clientId} onChange={(e) => set('clientId', e.target.value)}>
              <option value="">Все клиенты</option>
              {clientsOf(invoices).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <select className="field h-9 w-44 py-0" aria-label="Статус" value={filter.status} onChange={(e) => set('status', e.target.value as InvoiceStatus | '')}>
              <option value="">Любой статус</option>
              {(Object.keys(invoiceStatus) as InvoiceStatus[]).filter((k) => k !== 'cancelled').map((k) => (
                <option key={k} value={k}>
                  {invoiceStatus[k].label}
                </option>
              ))}
            </select>
            <input className="field h-9 w-40 py-0" type="search" aria-label="Номер счёта" placeholder="Номер счёта" value={filter.q} onChange={(e) => set('q', e.target.value)} />
            <select className="field h-9 w-52 py-0" aria-label="Порядок" value={filter.sort} onChange={(e) => set('sort', e.target.value as InvoiceSort)}>
              {(Object.keys(sortLabel) as InvoiceSort[]).map((k) => (
                <option key={k} value={k}>
                  {sortLabel[k]}
                </option>
              ))}
            </select>
            <span className="text-xs text-muted">
              {rows.length === invoices.length ? `Счетов: ${rows.length}` : `Показано ${rows.length} из ${invoices.length}`}
            </span>
          </div>
          <div className="overflow-x-auto"><table className="w-full min-w-[640px] text-left text-sm">
            <thead className="text-muted">
              <tr className="border-b border-line">
                <th className="py-2 pr-4 font-normal">Счёт</th>
                <th className="py-2 pr-4 font-normal">Клиент</th>
                <th className="py-2 pr-4 font-normal">Срок</th>
                <th className="py-2 pr-4 text-right font-normal">Сумма</th>
                <th className="py-2 pr-4 font-normal">Оплата</th>
                {acts && <th className="py-2 pr-4 font-normal">Акт</th>}
                <th className="py-2 text-right font-normal">Статус</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((i) => {
                const s = invoiceStatus[i.status];
                const remindable = i.status === 'sent' || i.status === 'overdue';
                return (
                  <tr key={i.id}>
                    <td className="py-2.5 pr-4">
                      № {i.number} <span className="text-muted">от {formatDate(i.issuedAt)}</span>
                      <div className="text-xs text-muted">{i.lines.map((l) => l.title).join(', ')}</div>
                    </td>
                    <td className="py-2.5 pr-4">
                      {i.clientName ?? '—'}
                      {i.payer?.name && <span className="block text-xs text-muted">{i.payer.name}</span>}
                    </td>
                    <td className="py-2.5 pr-4 text-muted">{i.status === 'paid' ? formatDate(i.dueAt) : formatDue(i.dueAt)}</td>
                    <td className="num py-2.5 pr-4 text-right">{formatMoney(i.total)}</td>
                    <td className="py-2.5 pr-4 text-muted">{i.payment ? `${methodLabel[i.payment.method]}, ${i.payment.reference}` : '—'}</td>
                    {acts && (
                      <td className="py-2.5 pr-4">
                        {i.status !== 'paid' ? (
                          <span className="text-muted">—</span>
                        ) : i.actStatus === 'draft' ? (
                          <span className="text-muted">№ {i.actNumber}, черновик — проверьте и отправьте</span>
                        ) : i.actDocumentId ? (
                          <span>№ {i.actNumber}</span>
                        ) : (
                          <>
                            <span className="text-muted">{i.actId ? `№ ${i.actNumber}, PDF не сформирован` : 'акта нет'}</span>
                            <button className="btn-ghost ml-2 h-7 px-2 text-xs" disabled={issueAct.isPending} onClick={() => issueAct.mutate(i.id)}>
                              {i.actId ? 'Сформировать заново' : 'Подготовить черновик'}
                            </button>
                          </>
                        )}
                      </td>
                    )}
                    <td className="py-2.5 text-right">
                      <div className="flex flex-wrap items-center justify-end gap-1">
                        <Status tone={s.tone}>{s.label}</Status>
                        {i.status === 'draft' && <InvoiceDraftActions invoice={i} />}
                        {remindable && (
                          <button className="btn-ghost h-7 px-2 text-xs" disabled={remind.isPending || remindedRecently(i)} onClick={() => remind.mutate(i.id)}>
                            {remindedRecently(i) ? 'Напомнили' : 'Напомнить'}
                          </button>
                        )}
                        <DownloadButton className="btn-ghost size-7 px-0" load={() => studioApi.invoicePdf(i.id)} name={`invoice-${i.number}.pdf`} label={`PDF счёта № ${i.number}`}>
                          <FileText className="size-3.5" />
                        </DownloadButton>
                        {i.inUrl && (
                          <a className="btn-ghost h-7 px-2 text-xs" href={i.inUrl} target="_blank" rel="noreferrer" aria-label={`Открыть счёт № ${i.number} в IN`}>
                            <ExternalLink className="size-3.5" /> IN
                          </a>
                        )}
                        {markable(i) && (
                          <button className="btn-ghost h-7 px-2 text-xs" onClick={() => setMarking(i)}>
                            Отметить оплату
                          </button>
                        )}
                        {cancellable(i) && (
                          <button className="btn-ghost h-7 px-2 text-xs text-overdue" onClick={() => setCancelling(i)}>
                            Отменить
                          </button>
                        )}
                      </div>
                      {i.remindedAt && <div className="text-xs text-faint">напоминали {formatWhen(i.remindedAt)}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
          {!rows.length && <p className="py-6 text-center text-sm text-muted">{invoices.length ? 'По этим условиям счетов нет.' : 'Счетов пока нет.'}</p>}
        </>
      )}
      <MarkPaidDialog invoice={marking} onClose={() => setMarking(undefined)} />
      <CancelInvoice invoice={cancelling} onClose={() => setCancelling(undefined)} />
    </>
  );
}

/** Cancelling is final in Invoice Ninja, so it asks first, with the number and the total in front of the person. */
function CancelInvoice({ invoice, onClose }: { invoice?: Invoice; onClose: () => void }) {
  const cancel = useCancelInvoice();
  return (
    <Dialog open={!!invoice} onClose={onClose} title="Отменить счёт?">
      {invoice && (
        <div className="space-y-4 p-5 text-sm">
          <p>
            Счёт № {invoice.number}{invoice.clientName ? ` для «${invoice.clientName}»` : ''} на {formatMoney(invoice.total)} будет отменён в Invoice Ninja и исчезнет из списка. Клиент перестанет видеть его к оплате. Вернуть отменённый счёт нельзя: выставьте новый.
          </p>
          {cancel.error && (
            <p role="alert" className="text-overdue">
              {cancel.error.message}
            </p>
          )}
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={onClose}>
              Не отменять
            </button>
            <button className="btn-primary flex-1" disabled={cancel.isPending} onClick={() => cancel.mutate(invoice.id, { onSuccess: onClose })}>
              Отменить счёт
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

/** Online payments the portal could not settle: the studio looks into each and marks it handled with a note. */
function PaymentProblems() {
  const { data } = useCheckoutProblems();
  const resolve = useResolveCheckout();
  const [notes, setNotes] = useState<Record<string, string>>({});
  if (!data?.length) return null;

  return (
    <section className="mb-10 rounded-lg p-5 shadow-hair" aria-label="Проблемы с оплатами">
      <h2 className="text-sm font-semibold">Проблемы с оплатами: {data.length}</h2>
      <p className="mt-1 text-sm text-muted">Банк принял платёж, а портал не смог его учесть как обычно. Возврат делается вручную в кабинете банка; здесь только отметка «разобрано».</p>
      <ul className="mt-3 divide-y divide-line">
        {data.map((c) => (
          <li key={c.id} className="flex flex-wrap items-center gap-3 py-2.5 text-sm">
            <span className="min-w-0 flex-1">
              Счёт № {c.invoiceNumber ?? c.invoiceId}: {c.problem ? problemLabel[c.problem] : c.status}
              <span className="block text-xs text-muted">
                ждали {formatMoney(c.amount)}
                {c.bankAmount ? `, банк ${formatMoney(c.bankAmount)}` : ''}
                {c.overpaid ? `, лишнее ${formatMoney(c.overpaid)}` : ''}, операция {c.operationId}
              </span>
            </span>
            <input className="field h-8 w-56 py-0" placeholder="Что сделали" value={notes[c.id] ?? ''} onChange={(e) => setNotes((n) => ({ ...n, [c.id]: e.target.value }))} />
            <button className="btn-primary h-8" disabled={!(notes[c.id] ?? '').trim() || resolve.isPending} onClick={() => resolve.mutate({ id: c.id, note: (notes[c.id] ?? '').trim() })}>
              Разобрано
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
